import { useState, useEffect } from 'react';
import { useTenant } from '../../context/TenantContext'; 
import { createTenantW2Employee } from '../../services/crmService'; 
import { functions, db } from '../../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, where } from 'firebase/firestore';

export default function W2IntakeForm({ onClose }) {
  const { tenantId, loading: tenantLoading } = useTenant(); 
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Onboarding Method Toggle: 'vault' (Internal FTL Flow) vs 'checkhq' (Automated Invite)
  const [onboardingMode, setOnboardingMode] = useState('vault');

  // Dynamic Locations State
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Lightweight payload. No SSN or Banking PII handled here.
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    corporateEmail: '',
    personalEmail: '',
    phone: '',
    role: 'Retail Sales Associate', // HR / Display Title
    systemRole: 'ASSOCIATE', // Maps to Apex OS RBAC / PBAC
    location: '',
    payType: 'Hourly',
    payRate: '',
    notes: ''
  });

  // --- FETCH ACTIVE SUB-LOCATIONS/DIVISIONS ---
  useEffect(() => {
    const fetchLocations = async () => {
      if (tenantLoading || !tenantId) return;
      
      setLoadingLocations(true);
      try {
        const storesRef = collection(db, 'clients', tenantId, 'stores');
        // Query for locations/doors that are active
        const q = query(storesRef, where('isActive', '==', true));
        const snapshot = await getDocs(q);
        
        const fetchedLocations = [];
        snapshot.forEach(doc => {
          fetchedLocations.push({ id: doc.id, ...doc.data() });
        });
        
        setLocations(fetchedLocations);
      } catch (error) {
        console.error("System Error: Failed to fetch operational locations.", error);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchLocations();
  }, [tenantId, tenantLoading]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!tenantId) {
      alert("System Error: Corporate identity not resolved.");
      return;
    }

    setIsSubmitting(true);
    
    try {
      // --- DYNAMIC HOME STORE ID ROUTING ---
      const matchedLocation = locations.find(loc => (loc.storeName || loc.name) === formData.location);
      
      // Force corporate bucket for regional directors or remote staff, otherwise assign the exact physical door ID
      const derivedHomeStoreId = ['REGIONAL_DIRECTOR', 'MASTER_AGENT'].includes(formData.systemRole) 
        ? 'store_corporate_reinhardt' 
        : (matchedLocation ? matchedLocation.id : 'store_corporate_reinhardt');

      if (onboardingMode === 'vault') {
        // --- 1. DIRECT IN-HOUSE PROVISIONING (eeKoSystem FTL VAULT FLOW) ---
        // GOLD STANDARD FIX: Synchronous Callable Function replaces passive DB write
        const provisionVaultWorkerFn = httpsCallable(functions, 'provisionVaultWorker');
        
        const result = await provisionVaultWorkerFn({
          tenantId: tenantId,
          workerType: 'employee',
          systemRole: formData.systemRole, // Pass role to backend for precise PBAC mapping
          homeStoreId: derivedHomeStoreId, // <--- INJECTED STORE ID
          firstName: formData.firstName,
          lastName: formData.lastName,
          corporateEmail: formData.corporateEmail,
          personalEmail: formData.personalEmail,
          phone: formData.phone,
          hrRole: formData.role, // HR Display Title
          location: formData.location,
          assignedTenant: formData.location, // Sync generic operational assignment
          payType: formData.payType,
          payRate: Number(formData.payRate)
        });

        if (result.data.success) {
          alert("W-2 Employee successfully minted! Secure FTL Vault link dispatched.");
          onClose();
        } else {
          throw new Error(result.data.message || "Unknown provisioning error.");
        }

      } else {
        // --- 2. CHECKHQ AUTOMATED DISPATCH FLOW ---
        const newEmployee = await createTenantW2Employee(tenantId, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          corporateEmail: formData.corporateEmail,
          personalEmail: formData.personalEmail,
          email: formData.corporateEmail, // Fallback for CRM UI
          phone: formData.phone,
          role: formData.role,
          systemRole: formData.systemRole,
          location: formData.location,
          assignedTenant: formData.location,
          homeStoreId: derivedHomeStoreId, // <--- INJECTED STORE ID
          payType: formData.payType,
          payRate: Number(formData.payRate),
          // CheckHQ Lock Trigger
          status: 'pre_launch',
          complianceStatus: 'pending_checkhq',
          onboardingMethod: 'checkhq_invite',
          checkhq_id: null
        });

        const generateOnboardLinkFn = httpsCallable(functions, 'generateCheckOnboardLink');
        await generateOnboardLinkFn({ 
          tenantId, 
          workerId: newEmployee.id, 
          workerType: 'employee',
          email: formData.personalEmail, // Dispatch to personal inbox
          firstName: formData.firstName,
          lastName: formData.lastName
        });

        alert("CheckHQ secure onboarding link generated and dispatched!");
        onClose();
      }
    } catch (error) {
      console.error("Failed to provision W-2 employee:", error);
      alert(`System Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/50 backdrop-blur-sm animate-in fade-in">
      <div className="w-full max-w-2xl bg-slate-50 h-full border-l border-slate-300 shadow-2xl flex flex-col slide-in-from-right">
        
        {/* Header */}
        <div className="bg-slate-900 p-6 border-b border-slate-700 flex justify-between items-center shrink-0">
          <div>
            <h2 className="text-xl font-black text-white uppercase tracking-tight">W-2 Employee Onboarding</h2>
            <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest mt-1">Personnel Provisioning Record</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <i className="fa-solid fa-xmark text-xl"></i>
          </button>
        </div>

        {/* Onboarding Mode Selector */}
        <div className="bg-white border-b border-slate-200 p-4 shrink-0 flex gap-4">
          <button 
            type="button"
            onClick={() => setOnboardingMode('vault')}
            className={`flex-1 py-3 px-4 text-left border transition-all ${
              onboardingMode === 'vault' 
                ? 'border-emerald-500 bg-emerald-50/50 shadow-sm' 
                : 'border-slate-200 hover:border-slate-300 bg-slate-50'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-black text-slate-900 uppercase tracking-wider">eeKoSystem Vault</span>
              <span className="text-[8px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 uppercase tracking-widest">In-House FTL</span>
            </div>
            <p className="text-[9px] text-slate-500 mt-1">Employee completes W-4, I-9 & ACH via internal First Time Login.</p>
          </button>

          <button 
            type="button"
            onClick={() => setOnboardingMode('checkhq')}
            className={`flex-1 py-3 px-4 text-left border transition-all ${
              onboardingMode === 'checkhq' 
                ? 'border-emerald-500 bg-emerald-50/50 shadow-sm' 
                : 'border-slate-200 hover:border-slate-300 bg-slate-50'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-black text-slate-900 uppercase tracking-wider">CheckHQ Invite</span>
              <span className="text-[8px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 uppercase tracking-widest">API Flow</span>
            </div>
            <p className="text-[9px] text-slate-500 mt-1">Dispatches CheckHQ link for external compliance & payroll setup.</p>
          </button>
        </div>

        {/* High-Density Form */}
        <form onSubmit={handleSubmit} id="w2-form" className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
          
          {/* Section 1: Identity */}
          <div className="bg-white border border-slate-200 p-5 shadow-sm rounded-sm">
            <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100 pb-2 mb-4">Identity & Contact</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">First Name <span className="text-red-500">*</span></label>
                <input required type="text" name="firstName" value={formData.firstName} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Last Name <span className="text-red-500">*</span></label>
                <input required type="text" name="lastName" value={formData.lastName} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Corporate Email (Login) <span className="text-red-500">*</span></label>
                <input required type="email" name="corporateEmail" value={formData.corporateEmail} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Personal Email (Dispatch) <span className="text-red-500">*</span></label>
                <input required type="email" name="personalEmail" value={formData.personalEmail} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Mobile Phone <span className="text-red-500">*</span></label>
                <input required type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" />
              </div>
            </div>
          </div>

          {/* Section 2: Operational Assignment */}
          <div className="bg-white border border-slate-200 p-5 shadow-sm rounded-sm">
            <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100 pb-2 mb-4">Operational Assignment</h3>
            <div className="grid grid-cols-2 gap-4">
              
              <div className="col-span-2">
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">System Role & Clearance <span className="text-red-500">*</span></label>
                <select required name="systemRole" value={formData.systemRole} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none cursor-pointer rounded-sm">
                  <option value="ASSOCIATE">Field Rep / Associate (Standard Access)</option>
                  <option value="STORE_MANAGER">Store Manager (Location Override)</option>
                  <option value="REGIONAL_DIRECTOR">Regional Director (Multi-Location Override)</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">HR Title (Display) <span className="text-red-500">*</span></label>
                <select required name="role" value={formData.role} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none cursor-pointer rounded-sm">
                  <option value="Retail Sales Associate">Retail Sales Associate</option>
                  <option value="Store Manager">Store Manager</option>
                  <option value="Multi-Unit Manager">Multi-Unit Manager</option>
                  <option value="Maintenance Technician">Maintenance Technician</option>
                  <option value="Corporate Staff">Corporate Staff</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Store / Location <span className="text-red-500">*</span></label>
                <select 
                  required 
                  name="location" 
                  value={formData.location} 
                  onChange={handleChange} 
                  disabled={loadingLocations}
                  className="w-full bg-slate-50 border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none cursor-pointer rounded-sm disabled:opacity-50"
                >
                  <option value="">{loadingLocations ? 'Fetching locations...' : 'Select Location...'}</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.storeName || loc.name}>
                      {loc.storeName || loc.name} {loc.city ? `(${loc.city})` : ''}
                    </option>
                  ))}
                  <option value="Remote / Corporate">Remote / Corporate</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Pay Structure <span className="text-red-500">*</span></label>
                <select required name="payType" value={formData.payType} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none cursor-pointer rounded-sm">
                  <option value="Hourly">Hourly Non-Exempt</option>
                  <option value="Salary">Salary Exempt</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Pay Rate <span className="text-red-500">*</span></label>
                <div className="flex">
                  <span className="bg-slate-100 border border-slate-300 border-r-0 px-3 py-2 text-sm font-bold text-slate-500 rounded-l-sm">$</span>
                  <input required type="number" step="0.01" name="payRate" value={formData.payRate} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-r-sm" />
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Vault Action Explainer */}
          {onboardingMode === 'vault' ? (
            <div className="bg-slate-100 border border-slate-300 p-5 rounded-sm flex items-start gap-4">
              <div className="w-10 h-10 bg-white border border-slate-300 text-slate-600 rounded-full flex items-center justify-center shrink-0">
                <i className="fa-solid fa-vault"></i>
              </div>
              <div>
                <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest">Compliance Handoff (FTL Lock)</h4>
                <p className="text-[10px] font-bold text-slate-500 mt-1 leading-relaxed">
                  Upon dispatch, this employee's CRM profile will be created and locked in a <strong>pre_launch</strong> status. They will receive an email prompting their First Time Login (FTL), where they must securely input their W-4 data, I-9 verification, ACH routing, and uniform sizing before accessing the system.
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 p-5 rounded-sm flex items-start gap-4">
              <div className="w-10 h-10 bg-white border border-amber-200 text-amber-600 rounded-full flex items-center justify-center shrink-0">
                <i className="fa-solid fa-envelope-open-text"></i>
              </div>
              <div>
                <h4 className="text-xs font-black text-amber-900 uppercase tracking-widest">External API Handoff</h4>
                <p className="text-[10px] font-bold text-amber-700 mt-1 leading-relaxed">
                  This routes the employee entirely through CheckHQ. They will receive an automated email from CheckHQ to establish their tax profile and direct deposit settings. Their CRM status will read <strong>pending_checkhq</strong> until the API confirms clearance.
                </p>
              </div>
            </div>
          )}

        </form>

        {/* Footer Actions */}
        <div className="p-4 border-t border-slate-300 bg-white flex justify-end gap-3 shrink-0">
          <button onClick={onClose} className="px-6 py-2 text-[10px] font-bold text-slate-600 border border-slate-300 hover:bg-slate-50 uppercase tracking-widest transition-colors rounded-sm">
            Cancel
          </button>
          <button 
            type="submit"
            form="w2-form"
            disabled={isSubmitting || tenantLoading || loadingLocations}
            className="px-6 py-2 text-[10px] font-bold text-white bg-emerald-600 border border-emerald-600 hover:bg-emerald-700 uppercase tracking-widest transition-colors shadow-sm disabled:opacity-50 rounded-sm flex items-center gap-2"
          >
            {tenantLoading ? 'Authenticating...' : (isSubmitting ? <><i className="fa-solid fa-spinner fa-spin"></i> Vaulting...</> : (onboardingMode === 'vault' ? <><i className="fa-solid fa-paper-plane"></i> Dispatch FTL Link</> : <><i className="fa-solid fa-paper-plane"></i> Dispatch CheckHQ Invite</>))}
          </button>
        </div>

      </div>
    </div>
  );
}